import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import { Column } from 'primereact/column';
import { EnnoviImportExpeditionRowRequestInDTO } from '@api/ringil-api.v2';
import { useCreateInputPasteTabular } from '@components/framework/input/InputPasteTabular/InputPasteTabular.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SvgPlay from '@icons/play.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgFile from '@icons/file-inverted.svg?react';
import SvgAlert from '@icons/alert.svg?react';
import SvgSuccess from '@icons/check-circle.svg?react';
import SvgRepeat from '@icons/repeat.svg?react';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { exportExcel } from '@utils/exports.ts';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';


interface ComponentProps {
}

interface ImportExpedition {
  id?: string;
  delivery: string;
  shipToParty: string;
  shipFromParty: string;
  loadingDate: string;
  unloadingDate: string;
  material: string;
  quantity: string;
  status?: string;
  errorMessages?: string[];
  expeditionLink?: string;
  expeditionApplicationId?: string;
  incotermsValue?: string;
  incotermsDescription?: string;
}

interface ImportResult {
  countSuccessRows: number;
  countCreatedExpeditions: number;
  countFailedRows: number;
}

const ExpeditionImport: React.FC<ComponentProps> = () => {

  const navigation = useNavigation();
  const { tr } = useTranslationLgs();

  const { importEnnoviExpedition, checkImportEnnoviExpedition } = useContext(ApiContext);
  const [importData, setImportData] = useState([]);
  const [importEnabled, setImportEnabled] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult>(null);
  const [loading, setLoading] = useState(false);

  const { InputPasteTabular, PasteTabularButton } = useCreateInputPasteTabular<ImportExpedition>([
    'delivery',
    'shipFromParty',
    'shipToParty',
    'loadingDate',
    'unloadingDate',
    'material',
    'quantity',
    'incotermsValue',
    'incotermsDescription',
  ]);

  const importAction = () => {
    setImportResult(null);
    setImportEnabled(false);
    setLoading(true);

    importEnnoviExpedition(
      { rows: importData },
      data => {
        const withStatus = importData.map(row => {
          const matchedRow = data.rows?.find(r => r.id === row.id);
          return {
            ...row,
            errorMessages: matchedRow?.isSuccess ? null : [tr(matchedRow?.errorMessage)],
            expeditionLink: matchedRow?.isSuccess && matchedRow?.expeditionCoordinate?.expeditionId
              ? navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(matchedRow.expeditionCoordinate.expeditionId))
              : null,
            expeditionApplicationId: matchedRow?.expeditionCoordinate?.applicationId,
          };
        });

        setImportResult({
          countSuccessRows: data.countSuccessRows,
          countCreatedExpeditions: data.countCreatedExpeditions,
          countFailedRows: data.countFailedRows,
        });

        setImportData(withStatus);
        setLoading(false);

      },
    );
  };

  function parseNumberString(value: string): string {
    return value.replace(/\s+/g, '');
  }

  const handleCheckImport = (data: ImportExpedition[]) => {
    setLoading(true);
    setValidationError(false);
    const converted: EnnoviImportExpeditionRowRequestInDTO[] = data.map((item, index) => ({
      ...item,
      id: String(index),
      quantity: item.quantity ? parseNumberString(item.quantity) : item.quantity,
    }));

    checkImportEnnoviExpedition({ rows: converted },
      data => {
        const withStatus = converted.map(row => {
          const matchedRow = data.rows?.find(r => r.id === row.id);
          return {
            ...row,
            errorMessages: matchedRow?.errorMessages
              ? matchedRow.errorMessages.map(errorMessage => tr(errorMessage))
              : null,
          };
        });
        setImportData(withStatus);
        const hasError = withStatus.some(row => row.errorMessages);
        setImportEnabled(!hasError);
        setValidationError(hasError);
        setLoading(false);
      });
  };


  const exportExcelHandler = () => {
    const hasApplicationId = importData.some(d => !!d.expeditionApplicationId);
    const hasExpeditionLink = importData.some(d => !!d.expeditionLink);
    exportExcel(
      importData.map(data => {
        const row: any = {
          delivery: data.delivery,
          shipToParty: data.shipToParty,
          shipFromParty: data.shipFromParty,
          loadingDate: data.loadingDate,
          unloadingDate: data.unloadingDate,
          material: data.material,
          quantity: data.quantity,
          incotermsValue: data.incotermsValue,
          incotermsDescription: data.incotermsDescription,
        };
        if (hasApplicationId && data.expeditionApplicationId) {
          row.applicationId = data.expeditionApplicationId;
        }
        if (hasExpeditionLink && data.expeditionLink) {
          row.expeditionLink = `${window.location.origin}${data.expeditionLink}`;
        }
        row.status = data.errorMessages?.length ? data.errorMessages.join(', ') : '';
        return row;
      }),
      'shipments-import'
    );
  };

  return (
    <div className={'r-legacy-import-fields flex flex-col gap-2'}>
      <BreadCrumbComponent
        items={[
          {
            label: tr(`BreadCrumb.expeditions`, `All shipments`),
            url: navigation.createNavigationLink(navigation.urlFunctions.createCustomerExpeditionList()),
          },
          {
            label: tr(`BreadCrumb.expeditionImport`, `Shipment import`),
          },
        ]} />
      <div className="flex mb-6">
        <COHeadline title={tr('ExpeditionImport.title', 'Expedition import')} variant={'h2'} />
        {importData.length > 0 && (
          <RingilButton
            mode={'text-only'}
            icon={<SvgPlus />}
            label={tr(`ExpeditionImport.newImport`, `New import`)}
            onClick={() => {
              setImportData([]);
              setImportEnabled(false);
              setValidationError(false);
              setImportResult(null);
            }}
          />
        )}
      </div>

      <InputPasteTabular onSave={handleCheckImport} />

      {importResult && importResult.countSuccessRows > 0 && (
        <COInlineMessage variant={'success'} icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}>
         <span className={'text-content-primary'}>
           {tr('ExpeditionImport.importSuccessMessage', 'Import was successful for {{itemCount}} items. We have created {{shipmentCount}} shipments.', {
             itemCount: importResult.countSuccessRows,
             shipmentCount: importResult.countCreatedExpeditions,
           })}
         </span>
        </COInlineMessage>
      )}

      {importResult && importResult.countFailedRows > 0 && (
        <COInlineMessage
          variant={'error'}
          icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}
          buttons={<RingilButton
            icon={<SvgRepeat />}
            label={tr('ExpeditionImport.reimportButton', 'Re-upload data')}
            onClick={() => importAction()}
            mode={'outlined-rectangle'}
            size={'small'} /> }
          buttonsRight={true}
        >
         <span className={'text-content-primary'}>
           {tr('ExpeditionImport.importFailedMessage', 'Import failed for {{count}} items.', { count: importResult.countFailedRows })}
         </span>
        </COInlineMessage>
      )}

      {validationError && (
        <COInlineMessage variant={'error'} icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}>
         <span className={'text-content-primary'}>
           {tr('ExpeditionImport.validationError', 'Import cannot be completed. We have marked the items that do not comply with the requirements; please correct them and start a new import.')}
         </span>
        </COInlineMessage>
      )}

      {importEnabled && (
        <RingilButton
          icon={<SvgPlay />}
          label={tr('ExpeditionImport.importButton', 'Create shipments from uploaded data')}
          onClick={() => importAction()}
          mode={'filled-rectangle'}
          size={'small'} />
      )}

      {importData.length > 0 && (
        <div className="mt-4 bg-[#E7EBEE] p-4">
          <div className="flex justify-end items-center">
            <RingilButton
              mode={'text-only'}
              icon={<SvgFile />}
              label={tr('ExpeditionImport.exportCSV', 'Export to CSV')}
              onClick={exportExcelHandler}
            />
          </div>
        </div>
      )}
      <div className={'max-w-full'}>
        <DataTable
          value={importData}
          rowClassName={r => r.errorMessages ? 'bg-complementary-errorMedium !bg-complementary-errorMedium' : ''}
          loading={loading}
        >
          <Column
            field="delivery"
            body={r => r.delivery}
            header={tr(`ExpeditionImport.expeditionDelivery`, `Delivery`)}
          />
          <Column
            field="shipFromParty"
            body={r => r.shipFromParty}
            header={tr(`ExpeditionImport.expeditionShipFromParty`, `Ship from party`)}
          />
          <Column
            field="shipToParty"
            body={r => r.shipToParty}
            header={tr(`ExpeditionImport.expeditionShipToParty`, `Ship to party`)}
          />
          <Column
            field="loadingDate"
            body={r => r.loadingDate}
            header={tr(`ExpeditionImport.expeditionLoadingDate`, `Loading date`)}
          />
          <Column
            field="unloadingDate"
            body={r => r.unloadingDate}
            header={tr(`ExpeditionImport.expeditionUnloadingDate`, `Unloading date`)}
          />
          <Column
            field="material"
            body={r => r.material}
            header={tr(`ExpeditionImport.expeditionMaterial`, `Material`)}
          />
          <Column
            field="quantity"
            body={r => r.quantity}
            header={tr(`ExpeditionImport.expeditionQuantity`, `Quantity`)}
          />
          <Column
            field="incotermsValue"
            body={r => r.incotermsValue}
            header={tr(`ExpeditionImport.expeditionIncoterms`, `Incoterms`)}
          />
          <Column
            field="incotermsDescription"
            body={r => r.incotermsDescription}
            header={tr(`ExpeditionImport.expeditionIncotermsDescription`, `Incoterms description`)}
          />
          <Column
            body={r => (
              <div>
                {r.expeditionLink
                  ? <Link to={r.expeditionLink}>{r.expeditionApplicationId}</Link>
                  : r.errorMessages ? r.errorMessages.join(', ') : ''
                }
              </div>
            )}
            header="Status"
          />
        </DataTable>
        {importData.length === 0 && (
          <div className={'pt-4'}>
            <PasteTabularButton mode={'filled-rectangle'} />
          </div>
        )}
      </div>
      {importEnabled && (
        <RingilButton
          icon={<SvgPlay />}
          label={tr('ExpeditionImport.importButton', 'Create shipments from uploaded data')}
          onClick={() => importAction()}
          mode={'filled-rectangle'}
          size={'small'} />
      )}
    </div>
  )
    ;
};

export default ExpeditionImport;

